import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  ShoppingCart as PurchaseIcon,
  Send as QuoteIcon
} from '@mui/icons-material';
import axios from 'axios';

export default function MoverLeadDashboard({ token }) {
  const [availableLeads, setAvailableLeads] = useState([]);
  const [purchasedLeads, setPurchasedLeads] = useState([]);
  const [purchasedStats, setPurchasedStats] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Dialog states
  const [selectedLead, setSelectedLead] = useState(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  
  // Form states
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteMessage, setQuoteMessage] = useState('');
  const [filters, setFilters] = useState({
    tier: 'all',
    maxPrice: 25,
    minValue: 200
  });

  useEffect(() => {
    fetchLeadData();
  }, []);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      
      // Get mover profile to extract ID
      const profileRes = await axios.get('/api/movers/me', {
        withCredentials: true
      });
      const moverId = profileRes.data.mover._id;

      // Fetch available leads
      const leadsRes = await axios.get(`/api/leads/available-leads/${moverId}`, {
        params: filters
      });
      
      setAvailableLeads(leadsRes.data.leads || []);
      setSubscription(leadsRes.data.subscription);

      // Fetch purchased leads and analytics
      const purchasedRes = await axios.get('/api/leads/my-purchased', {
        params: { moverId },
        withCredentials: true,
      });
      setPurchasedLeads(purchasedRes.data.leads || []);
      setPurchasedStats(purchasedRes.data.stats || {});
      
    } catch (err) {
      setError('Failed to load lead data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseLead = async (lead) => {
    try {
      const profileRes = await axios.get('/api/movers/me', {
        withCredentials: true
      });
      const moverId = profileRes.data.mover._id;

      const response = await axios.post('/api/leads/purchase-lead', {
        leadId: lead._id,
        moverId,
        purchaseType: 'standard'
      }, { withCredentials: true });

      if (response.data.success) {
        setShowPurchaseDialog(false);
        fetchLeadData(); // Refresh data
        
        // Show contact reveal dialog
        setSelectedLead({
          ...lead,
          contactRevealCode: response.data.leadDetails.contactRevealCode,
          expiresAt: response.data.leadDetails.expiresAt
        });
        setShowContactDialog(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to purchase lead');
    }
  };

  const handleRevealContact = async () => {
    try {
      const profileRes = await axios.get('/api/movers/me', {
        withCredentials: true
      });
      const moverId = profileRes.data.mover._id;

      const response = await axios.post('/api/leads/reveal-contact', {
        leadId: selectedLead._id,
        moverId,
        revealCode: selectedLead.contactRevealCode
      }, { withCredentials: true });

      setSelectedLead({
        ...selectedLead,
        customerContact: response.data.customerContact,
        fullMoveDetails: response.data.moveDetails
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reveal contact');
    }
  };

  const handleSubmitQuote = async () => {
    try {
      const profileRes = await axios.get('/api/movers/me', {
        withCredentials: true
      });
      const moverId = profileRes.data.mover._id;

      await axios.post('/api/leads/track-quote', {
        leadId: selectedLead._id,
        moverId,
        quoteAmount: parseFloat(quoteAmount),
        responseMessage: quoteMessage
      }, { withCredentials: true });

      setShowQuoteDialog(false);
      setQuoteAmount('');
      setQuoteMessage('');
      alert('Quote submitted successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit quote');
    }
  };

  const getLeadTierColor = (tier) => {
    switch (tier) {
      case 'premium': return 'success';
      case 'standard': return 'primary';
      case 'basic': return 'default';
      default: return 'default';
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const hours = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60)));
    return `${hours}h remaining`;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading lead marketplace...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Subscription Info */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Lead Marketplace
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Purchase qualified moving leads in your service area
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={4}>
          {subscription && (
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" color="primary" sx={{ textTransform: 'capitalize' }}>
                  {subscription.tier} Plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subscription.remainingLeads} leads remaining this month
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Account Balance: ${subscription.accountBalance?.toFixed(2) || '0.00'}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Filter Leads</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Lead Tier</InputLabel>
                <Select
                  value={filters.tier}
                  onChange={(e) => setFilters({...filters, tier: e.target.value})}
                >
                  <MenuItem value="all">All Tiers</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="basic">Basic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max Lead Price"
                type="number"
                value={filters.maxPrice}
                onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Min Job Value"
                type="number"
                value={filters.minValue}
                onChange={(e) => setFilters({...filters, minValue: e.target.value})}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
          </Grid>
          
          <Button
            variant="contained"
            onClick={fetchLeadData}
            sx={{ mt: 2 }}
          >
            Apply Filters
          </Button>
        </CardContent>
      </Card>

      {/* Available Leads Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Leads ({availableLeads.length})
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Route</TableCell>
                  <TableCell>Move Date</TableCell>
                  <TableCell>Job Value</TableCell>
                  <TableCell>Lead Tier</TableCell>
                  <TableCell>Lead Price</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableLeads.map((lead) => (
                  <TableRow key={lead._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {lead.moveDetails.origin.split(',')[0]} →
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.moveDetails.destination.split(',')[0]}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(lead.moveDetails.moveDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lead.moveDetails.moveType}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        ${lead.moveDetails.estimatedValue}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={lead.leadTier}
                        color={getLeadTierColor(lead.leadTier)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        ${lead.leadPrice}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StarIcon sx={{ fontSize: 16, mr: 0.5, color: 'gold' }} />
                        <Typography variant="body2">{lead.leadScore}/10</Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeRemaining(lead.expiresAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Tooltip title="Purchase Lead">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowPurchaseDialog(true);
                          }}
                          disabled={subscription?.remainingLeads <= 0 && subscription?.accountBalance < lead.leadPrice}
                        >
                          <PurchaseIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {availableLeads.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No leads available matching your criteria
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Purchased Leads */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">My Purchased Leads ({purchasedLeads.length})</Typography>
            {purchasedStats.totalSpent > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2" color="text.secondary">Spent: <strong>${purchasedStats.totalSpent?.toFixed(2)}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Converted: <strong>{purchasedStats.converted || 0}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Conversion: <strong>{purchasedStats.conversionRate || 0}%</strong></Typography>
              </Box>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Route</TableCell>
                  <TableCell>Move Date</TableCell>
                  <TableCell>Job Value</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Purchased</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchasedLeads.map(lead => (
                  <TableRow key={lead._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{lead.moveDetails?.origin?.split(',')[0]} → {lead.moveDetails?.destination?.split(',')[0]}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{lead.moveDetails?.moveDate ? new Date(lead.moveDetails.moveDate).toLocaleDateString() : '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">${lead.moveDetails?.estimatedValue || '—'}</Typography></TableCell>
                    <TableCell><Chip label={lead.leadTier || '—'} color={getLeadTierColor(lead.leadTier)} size="small" /></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600} color="primary">${lead.pricePaid?.toFixed(2) || '—'}</Typography></TableCell>
                    <TableCell><Chip label={lead.status || 'active'} size="small" color={lead.status === 'converted' ? 'success' : 'default'} /></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{lead.purchasedAt ? new Date(lead.purchasedAt).toLocaleDateString() : '—'}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {purchasedLeads.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No leads purchased yet</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showPurchaseDialog} onClose={() => setShowPurchaseDialog(false)}>
        <DialogTitle>Purchase Lead</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to purchase this lead for ${selectedLead.leadPrice}?
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Lead Details:</Typography>
              <Typography variant="body2">• Route: {selectedLead.moveDetails.origin} → {selectedLead.moveDetails.destination}</Typography>
              <Typography variant="body2">• Move Date: {new Date(selectedLead.moveDetails.moveDate).toLocaleDateString()}</Typography>
              <Typography variant="body2">• Estimated Value: ${selectedLead.moveDetails.estimatedValue}</Typography>
              <Typography variant="body2">• Lead Score: {selectedLead.leadScore}/10</Typography>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                You'll have 48 hours to contact the customer and submit a quote after purchase.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPurchaseDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handlePurchaseLead(selectedLead)}
            color="primary"
          >
            Purchase for ${selectedLead?.leadPrice}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Reveal Dialog */}
      <Dialog 
        open={showContactDialog} 
        onClose={() => setShowContactDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Lead Purchased Successfully!</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box>
              {!selectedLead.customerContact ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Lead purchased successfully! Click "Reveal Contact" to see customer details.
                  </Alert>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRevealContact}
                    startIcon={<ViewIcon />}
                    fullWidth
                  >
                    Reveal Customer Contact Information
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Customer Contact Information</Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Name:</Typography>
                        <Typography sx={{ ml: 1 }}>{selectedLead.customerContact.name || 'Not provided'}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography>{selectedLead.customerContact.email}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography>{selectedLead.customerContact.phone || 'Not provided'}</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Move Details:</Typography>
                      <Typography variant="body2">From: {selectedLead.fullMoveDetails?.origin}</Typography>
                      <Typography variant="body2">To: {selectedLead.fullMoveDetails?.destination}</Typography>
                      <Typography variant="body2">Date: {new Date(selectedLead.fullMoveDetails?.moveDate).toLocaleDateString()}</Typography>
                      <Typography variant="body2">Est. Value: ${selectedLead.fullMoveDetails?.estimatedValue}</Typography>
                    </Grid>
                  </Grid>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Important:</strong> You have {formatTimeRemaining(selectedLead.expiresAt)} to contact this customer. 
                      Quick response increases your booking chances!
                    </Typography>
                  </Alert>

                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      setShowContactDialog(false);
                      setShowQuoteDialog(true);
                    }}
                    startIcon={<QuoteIcon />}
                    fullWidth
                  >
                    Submit Quote for This Lead
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Quote Submission Dialog */}
      <Dialog open={showQuoteDialog} onClose={() => setShowQuoteDialog(false)}>
        <DialogTitle>Submit Quote</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Quote Amount"
            type="number"
            value={quoteAmount}
            onChange={(e) => setQuoteAmount(e.target.value)}
            InputProps={{ startAdornment: '$' }}
            sx={{ mb: 2, mt: 1 }}
          />
          
          <TextField
            fullWidth
            label="Message to Customer"
            multiline
            rows={3}
            value={quoteMessage}
            onChange={(e) => setQuoteMessage(e.target.value)}
            placeholder="Professional message explaining your quote..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitQuote}
            disabled={!quoteAmount || !quoteMessage}
          >
            Submit Quote
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}